import { CLASS_NAMES, EVENTS, MISC, SELECTORS } from '@bs/constants';

/**
 * A class for the Pie Chart Stat Counter
 */
export default class FwStatCounter {
  /**
   * Constructor for FwStatCounter
   * @param {HTMLElement} element - REQUIRED - the module's container
   * @param {object} Services - various services, passed in as param
   */
  constructor(element, Services) {
    this.element = element;
    this.ScrollService = Services.ScrollService;
    this.animating = false;
    this.animated = false;
    this.resizeTimeout = null;
    this.init();
  }

  /**
   * Initializes the view
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Find and cache necessary DOM elements
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  cacheDomReferences() {
    this.counter = this.element.querySelector(
      `.${CLASS_NAMES.FW_STAT_COUNTER}`
    );
    this.counterDiv = this.counter?.querySelector(
      `.${CLASS_NAMES.FW_STAT_COUNTER_VALUE}`
    );
    return this;
  }

  /**
   * Bind event handlers
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  setupHandlers() {
    this.onScrollHandler = this.onScroll.bind(this);
    this.onResizeHandler = this.onResize.bind(this);
    return this;
  }

  /**
   * Enable event listeners
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  enable() {
    window.setTimeout(this.onScrollHandler, 300);
    this.ScrollService.addCallback(this.onScrollHandler);
    window.addEventListener(EVENTS.RESIZE, this.onResizeHandler);
    return this;
  }

  /**
   * Triggers the counter once it's in the viewport
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  onScroll() {
    if (
      this.counter.dataset.visible === MISC.TRUE &&
      !this.animating &&
      !this.animated
    ) {
      this.countUp();
    }
    return this;
  }

  /**
   * Animates the counting of a number (percentage or number)
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  countUp() {
    const targetValue = parseFloat(
      this.counter.dataset.targetPct || this.counter.dataset.targetNum
    );
    if (!Number.isNaN(targetValue)) {
      this.element.dispatchEvent(
        new CustomEvent('fwStatCounter:start', { bubbles: true })
      );
      this.animateNumber(targetValue, 1000);
    }
    return this;
  }

  /**
   * A function which animates the counting of a number using a sliding effect.
   * @param {number} target - the number to animate to
   * @param {number} duration - how long to take to animate, in milliseconds
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  animateNumber(target, duration) {
    if (!target || !this.counterDiv) return this;
    this.animating = true;
    this.counterDiv.innerHTML = ''; // Clear existing content

    // Generate numbers from 0 to target
    let numbersHTML = '';
    for (let i = 0; i <= target; i += 1) {
      numbersHTML += `<div>${i}${
        this.counter.hasAttribute(`${SELECTORS.DATA_TARGET_PCT}`) ? '%' : ''
      }</div>`;
    }
    this.counterDiv.innerHTML = numbersHTML;

    // Get updated height values
    const firstChildHeight = this.counterDiv.firstElementChild.offsetHeight;
    const totalHeight = this.counterDiv.offsetHeight - firstChildHeight;

    let startTime = null;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;

      const progress = Math.min(elapsed / duration, 1);
      const translateY = progress * totalHeight;

      this.counterDiv.style.transform = `translateY(-${translateY}px)`;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.animating = false;
        this.animated = true;
      }
    };

    requestAnimationFrame(animate.bind(this));

    return this;
  }

  /**
   * Handles window resize events to adjust counter animation if needed
   */
  onResize() {
    clearTimeout(this.resizeTimeout);

    this.resizeTimeout = setTimeout(() => {
      if (this.animated) {
        // Recalculate height and restart animation
        this.animated = false;
        this.countUp();
      }
    }, 300);
  }
}
